import React, { ChangeEvent, forwardRef, useMemo, useRef } from "react"

import FeatherIcon from "../../design-system/feather-icon"
import Input, { InputProps } from "../../design-system/input"
import { eventNames, track } from "../../utils/analytics"
import { classNames } from "../../utils/class-names"
import { debounce } from "../../utils/debounce"
import * as styles from "./index.module.scss"

type Props = InputProps & {
  onSearchQueryChange: (value: string) => void
  searchQuery: string | undefined
  variant?: "large" | "xmedium"
  className?: string
  onPressEnter?: () => void
  trackingProperties?: Record<string, string | number>
}

/** Input with a search icon, and tracking of search queries */
const SearchInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    onSearchQueryChange,
    searchQuery,
    className,
    onPressEnter,
    trackingProperties,
    ...otherProps
  } = props

  // Track queries, but don't double-count
  const trackedQueries = useRef<string[]>([])

  const debouncedTracking = useMemo(
    () =>
      debounce((query: string | undefined, eventType: "change" | "blur") => {
        if (!trackingProperties) return

        const trimmedQuery = query?.trim().toLowerCase() ?? ""
        const isChange = eventType === "change"
        const skipTracking =
          isChange &&
          (!trimmedQuery || trackedQueries.current.includes(trimmedQuery))

        if (skipTracking) return

        trackedQueries.current.push(trimmedQuery)

        const eventName =
          eventNames[`searchQuery${isChange ? "Changed" : "Blurred"}`]
        track(eventName, {
          ...trackingProperties,
          formData: "searchString",
          searchQuery: trimmedQuery,
        })
      }, 2000),
    [trackingProperties],
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onSearchQueryChange(e.target.value)
    debouncedTracking(e.target.value, e.type === "change" ? "change" : "blur")
  }

  return (
    <Input
      ref={ref}
      placeholder="Search"
      prefix={() => <FeatherIcon type="search" className={styles.searchIcon} />}
      className={classNames(styles.searchInput, className)}
      onChange={handleChange}
      onBlur={handleChange}
      clearable
      value={searchQuery}
      type="search"
      onKeyDown={e => {
        if (e.key === "Enter") {
          onPressEnter?.()
        }
      }}
      {...otherProps}
    />
  )
})

SearchInput.displayName = "SearchInput"

export default SearchInput
